import React, { useEffect, useMemo } from 'react';
import {
  Box,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import StandingsSection from './StandingsSection.jsx';
import './StandingsWidget.css';

const STANDINGS_TABS = [
  { key: 'general', title: 'GÉNÉRAL', shortLabel: 'Général' },
  { key: 'home', title: 'DOMICILE', shortLabel: 'Domicile' },
  { key: 'away', title: 'EXTÉRIEUR', shortLabel: 'Extérieur' },
];

// const getTabIndexFromStatType = (statType) => {
//   const index = STANDINGS_TABS.findIndex((tab) => tab.key === statType);
//   return index === -1 ? 0 : index;
// };

const getTabIndexFromStatType = (statType, tabs = STANDINGS_TABS) => {
  const index = tabs.findIndex((tab) => tab.key === statType);
  return index === -1 ? 0 : index;
};

const normalizeDayValue = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim();
  return normalized !== '' ? normalized : null;
};

const getDayDisplayLabel = (dayValue) => `Journée ${dayValue}`;

export default function StandingsWidget({
  discipline = 'rugby',
  competitionIds = [],
  competitionInfos = [],
  day = null,
  onNavigate,
  widgetConfigurationId = null,
  title,
  showCompetitionInfos = true,
  statType = 'general',
  onStatTypeChange,
  groupKey = '',
  onGroupChange,
  onDayChange,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // const tabs = useMemo(() => STANDINGS_TABS, []);
  // const activeTab = getTabIndexFromStatType(statType);
  // const currentTab = tabs[activeTab];

  // Seulement pour coupe du monde 2025, on n'affiche que l'onglet général
  //supprimer/remplacer plus tard par une vraie config du type standingsDisplayMode, allowedStatTypes, maxStandingDay
  const isOptaC42025Competition = useMemo(() => {
      return (competitionInfos || []).some(
        (competition) =>
          String(competition?.thirdPartyId || '').toLowerCase() === 'opta' &&
          String(competition?.thirdPartyEntityId || '') === 'c4-2025'
      );
    }, [competitionInfos]);

    const tabs = useMemo(() => {
      if (isOptaC42025Competition) {
        return STANDINGS_TABS.filter((tab) => tab.key === 'general');
      }

      return STANDINGS_TABS;
    }, [isOptaC42025Competition]);

    const activeTab = getTabIndexFromStatType(statType, tabs);
    const currentTab = tabs[activeTab];

  const firstSeason = competitionInfos?.[0]?.season || '';
  const showHeader = Boolean(showCompetitionInfos && (title || firstSeason));

  const isMultiCompetition = competitionIds.length > 1;

  const handleTabChange = (_, value) => {
    const nextStatType = tabs[value]?.key || 'general';
    onStatTypeChange?.(nextStatType);
  };

  useEffect(() => {
    if (!isOptaC42025Competition || statType === 'general') return;

    onStatTypeChange?.('general');
  }, [isOptaC42025Competition, statType, onStatTypeChange]);

  const groupOptions = useMemo(() => {
      const byKey = new Map();

      (competitionInfos || []).forEach((competition) => {
        if (
          competition?.rankingMode !== 'groups' ||
          !Array.isArray(competition?.groups)
        ) {
          return;
        }

        competition.groups.forEach((group) => {
          if (!group?.key || byKey.has(group.key)) return;

          byKey.set(group.key, {
            key: group.key,
            label: group.label || `Groupe ${group.key}`,
            order: Number(group.order) || 999,
          });
        });
      });

      return Array.from(byKey.values()).sort((a, b) => {
        const orderDiff = a.order - b.order;
        if (orderDiff !== 0) return orderDiff;
        return String(a.key).localeCompare(String(b.key));
      });
    }, [competitionInfos]);

   const dayOptions = useMemo(() => {
      if (isMultiCompetition) return [];

      const competition = competitionInfos?.[0] || {};
      const numericRound = Array.isArray(competition.rounds)
        ? competition.rounds.find((round) => round?.type === 'numeric')
        : null;

      let days = Array.isArray(numericRound?.days) && numericRound.days.length
        ? numericRound.days
        : Number(competition.nbDays) > 0
        ? Array.from({ length: Number(competition.nbDays) }, (_, index) =>
            String(index + 1)
          )
        : [];

      if (isOptaC42025Competition) {
        days = days.filter((value) => {
          const numericDay = Number(value);
          return Number.isFinite(numericDay) && numericDay >= 1 && numericDay <= 3;
        });
      }

      return days.map((value) => ({
        value: String(value),
        label: getDayDisplayLabel(value),
      }));
    }, [competitionInfos, isMultiCompetition, isOptaC42025Competition]);

    // const currentDayValue = day != null && day !== '' ? String(day) : '';
    // const currentDayIndex = dayOptions.findIndex(
    //   (option) => option.value === currentDayValue
    // );

    // const hasGroupFilter = groupOptions.length > 0;
    // const hasDayFilter = !isMultiCompetition && dayOptions.length > 0;

    const currentDayValue = normalizeDayValue(day) || '';
    const defaultDayValue = normalizeDayValue(competitionInfos?.[0]?.defaultDay) || '';

    const hasGroupFilter = !isMultiCompetition && groupOptions.length > 0;
    const hasDayFilter = !isMultiCompetition && dayOptions.length > 0;

    const lastDayValue = dayOptions[dayOptions.length - 1]?.value || '';

    const isCurrentDayValid =
      currentDayValue &&
      dayOptions.some((option) => String(option.value) === String(currentDayValue));

    const isDefaultDayValid =
      defaultDayValue &&
      dayOptions.some((option) => String(option.value) === String(defaultDayValue));

    const selectedDayValue = hasDayFilter
      ? isCurrentDayValid
        ? currentDayValue
        : isDefaultDayValid
        ? defaultDayValue
        : lastDayValue
      : currentDayValue;

    const currentDayIndex = dayOptions.findIndex(
      (option) => option.value === selectedDayValue
    );


    const handlePreviousDay = () => {
      if (currentDayIndex <= 0) return;
      onDayChange?.(dayOptions[currentDayIndex - 1].value);
    };

    const handleNextDay = () => {
      if (currentDayIndex === -1 || currentDayIndex >= dayOptions.length - 1) return;
      onDayChange?.(dayOptions[currentDayIndex + 1].value);
    };


    useEffect(() => {
      if (!hasDayFilter || !selectedDayValue || !onDayChange) return;

      if (String(currentDayValue || '') !== String(selectedDayValue)) {
        onDayChange(selectedDayValue);
      }
    }, [hasDayFilter, selectedDayValue, currentDayValue, onDayChange]);

    

    const firstGroupKey = groupOptions[0]?.key || '';

    const isCurrentGroupValid =
      groupKey &&
      groupOptions.some((group) => String(group.key) === String(groupKey));

    const selectedGroupKey = hasGroupFilter
      ? isCurrentGroupValid
        ? groupKey
        : firstGroupKey
      : '';

      useEffect(() => {
        if (!hasGroupFilter || !selectedGroupKey || !onGroupChange) return;

        if (String(groupKey || '') !== String(selectedGroupKey)) {
          onGroupChange(selectedGroupKey);
        }
      }, [hasGroupFilter, selectedGroupKey, groupKey, onGroupChange]);

  if (!competitionIds.length) return null;

  const showCompetitionData = Boolean(showCompetitionInfos) && Boolean(competitionInfos.length > 0) && Boolean(competitionIds.length > 1);

  return (
    <Box className="standings-widget">
      <Box className="standings-widget__inner">
        <Stack className="standings-widget__content">
          {showHeader ? (
            <Box className="standings-widget__header">
              {title ? (
                <Box className="standings-widget__title-wrap">
                  <span className="standings-widget__title-dot" aria-hidden="true" />
                  <Typography variant="h5" className="standings-widget__title">
                    {title}
                  </Typography>
                </Box>
              ) : null}

              {firstSeason ? (
                <Typography className="standings-widget__season">
                  {firstSeason}
                </Typography>
              ) : null}
            </Box>
          ) : null}

          {showCompetitionData ? (
            <Stack className="standings-widget__competition-list">
              {competitionInfos.map((competition) => (
                <Box
                  key={competition.id || competition.name}
                  className="standings-widget__competition-card"
                >
                  <Typography
                    variant="body1"
                    className="standings-widget__competition-name"
                  >
                    {competition.name}
                  </Typography>

                  {competition.season ? (
                    <Typography
                      variant="body2"
                      className="standings-widget__competition-season"
                    >
                      Saison {competition.season}
                    </Typography>
                  ) : null}
                </Box>
              ))}
            </Stack>
          ) : null}

          <Box className="standings-widget__stat-tabs-shell">
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              className="standings-widget__stat-tabs"
              TabIndicatorProps={{
                style: { display: 'none' },
              }}
            >
              {tabs.map((tab, index) => (
                <Tab
                  key={tab.key}
                  disableRipple
                  label={isMobile ? tab.shortLabel : tab.title}
                  className="standings-widget__stat-tab"
                />
              ))}
            </Tabs>
          </Box>
          <Box className="standings-widget__sections">
           <Box className="standings-widget__filters-row">
              {hasGroupFilter ? (
                <Box className="standings-widget__group-nav">
                  <FormControl
                    size="small"
                    variant="standard"
                    className="standings-widget__filter-select-control"
                  >
                    <Select
                      value={selectedGroupKey}
                      onChange={(event) => onGroupChange?.(event.target.value)}
                      displayEmpty
                      disableUnderline
                      MenuProps={{
                        PaperProps: {
                          className: 'standings-widget__menu-paper',
                        },
                      }}
                    >

                      {groupOptions.map((group) => (
                        <MenuItem
                          key={group.key}
                          value={group.key}
                          className="standings-widget__menu-item"
                        >
                          {group.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              ) : null}

              {hasDayFilter ? (
                <Box className="standings-widget__day-nav">
                  <IconButton
                    size="small"
                    onClick={handlePreviousDay}
                    disabled={currentDayIndex <= 0}
                    aria-label="Journée précédente"
                    className="standings-widget__day-button"
                  >
                    <ChevronLeftIcon fontSize="small" />
                  </IconButton>

                  <FormControl
                    size="small"
                    variant="standard"
                    className="standings-widget__filter-select-control"
                  >
                    <Select
                      value={selectedDayValue}
                      onChange={(event) => onDayChange?.(event.target.value)}
                      displayEmpty
                      disableUnderline
                      MenuProps={{
                        PaperProps: {
                          className: 'standings-widget__menu-paper',
                        },
                      }}
                    >
                      {dayOptions.map((option) => (
                        <MenuItem
                          key={option.value}
                          value={option.value}
                          className="standings-widget__menu-item"
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <IconButton
                    size="small"
                    onClick={handleNextDay}
                    disabled={
                      currentDayIndex === -1 || currentDayIndex >= dayOptions.length - 1
                    }
                    aria-label="Journée suivante"
                    className="standings-widget__day-button"
                  >
                    <ChevronRightIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : isMultiCompetition ? (
                <Typography variant="body2" className="standings-widget__multi-label">
                  Classement cumulé - toutes journées confondues
                </Typography>
              ) : selectedDayValue ? (
                <Typography variant="body2" className="standings-widget__day-label">
                  {getDayDisplayLabel(selectedDayValue)}
                </Typography>
              ) : null}
            </Box>

            {currentTab ? (
              <StandingsSection
                discipline={discipline}
                competitionIds={competitionIds}
                day={hasDayFilter ? selectedDayValue : day}
                groupKey={selectedGroupKey || null}
                statType={currentTab.key}
                widgetConfigurationId={widgetConfigurationId}
                onNavigate={onNavigate}
              />
            ) : null}

          </Box>

        </Stack>

      </Box>
    </Box>
  );
}
